import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Eye } from 'lucide-react';
import Header from '../components/Header';
import Alert from '../components/common/Alert';
import { useAuth } from '../hooks/useAuth';
import './MarketingSpendAnalysis.css';

const COLUMNS = [
  { key: 'day', label: 'Day', kind: 'date' },
  { key: 'weekday', label: 'Weekday', kind: 'text' },
  { key: 'overall', label: 'Overall', kind: 'money' },
  { key: 'socialMedia', label: 'Social Media', kind: 'money', manual: true },
  { key: 'googleAds', label: 'Google Ads', kind: 'money', manual: true },
  { key: 'googleOrganic', label: 'Google Organic', kind: 'money', manual: true },
  { key: 'utilityWa', label: 'Utility WA', kind: 'money' },
  { key: 'whatsappAbandon', label: 'WhatsApp Abandon', kind: 'money' },
  { key: 'whatsappCampaign', label: 'WhatsApp Campaign', kind: 'money' },
  { key: 'sms', label: 'SMS', kind: 'money' },
  { key: 'email', label: 'Email', kind: 'money' },
  { key: 'influencer', label: 'Influencer', kind: 'money', manual: true },
  { key: 'orders', label: 'Orders', kind: 'count' },
  { key: 'gmv', label: 'GMV', kind: 'money' },
  { key: 'aov', label: 'AOV', kind: 'money' },
  { key: 'roas', label: 'ROAS', kind: 'ratio' },
  { key: 'costPerOrder', label: 'Cost/Order', kind: 'money' },
  { key: 'ecoupon', label: 'Ecoupon', kind: 'money' },
  { key: 'loyalty', label: 'Loyalty', kind: 'money' },
  { key: 'storeCredit', label: 'Store Credit', kind: 'money' },
  { key: 'wallet', label: 'Wallet', kind: 'money' },
  { key: 'ecouponPct', label: 'Ecoupon %', kind: 'pct' },
  { key: 'loyaltyPct', label: 'Loyalty %', kind: 'pct' },
  { key: 'storeCreditPct', label: 'Store Credit %', kind: 'pct' },
  { key: 'walletPct', label: 'Wallet %', kind: 'pct' },
  { key: 'overallDiscount', label: 'Overall Discount', kind: 'pct' },
  { key: 'newCx', label: 'New Cx', kind: 'count' },
  { key: 'ordersFromOld', label: 'Old Cx', kind: 'count' },
  { key: 'retentionCost', label: 'Retention Cost', kind: 'money' },
  { key: 'cac', label: 'CAC', kind: 'money' },
  { key: 'ordersFromApp', label: 'Orders from App', kind: 'count' },
  { key: 'ordersFromAppPct', label: '% Orders from App', kind: 'pct' },
  { key: 'marketingNotes', label: 'Marketing Notes', kind: 'notes', manual: true }
];

const MONTH_EDIT_KEYS = new Set(['utilityWa', 'whatsappAbandon', 'whatsappCampaign']);

const AFFILIATE_COLUMN = { key: 'affiliate', label: 'Affiliate', kind: 'money', manual: true };

const MONTH_COLUMNS = [
  { key: 'monthLabel', label: 'Month', kind: 'text' },
  ...COLUMNS
    .filter((column) => column.key !== 'day' && column.key !== 'weekday')
    .flatMap((column) => {
      const next = MONTH_EDIT_KEYS.has(column.key) ? { ...column, manual: true } : column;
      return column.key === 'influencer' ? [next, AFFILIATE_COLUMN] : [next];
    })
];

const formatMonth = (monthKey) => {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  if (!year || !month) return monthKey || '';
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const lastDayKey = (monthKey) => {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  if (!year || !month) return '';
  const count = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${monthKey}-${String(count).padStart(2, '0')}`;
};

const currentMonthKey = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(now);
  const get = (type) => parts.find((part) => part.type === type).value;
  return `${get('year')}-${get('month')}`;
};

const money = (value) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(Number(value) || 0));
const ratio = (value) => (Number(value) || 0).toFixed(2);
const pct = (value) => `${(Number(value) || 0).toFixed(1)}%`;

const SPEND_KEYS = [
  'socialMedia',
  'googleAds',
  'googleOrganic',
  'utilityWa',
  'whatsappAbandon',
  'whatsappCampaign',
  'sms',
  'email',
  'influencer',
  'affiliate'
];

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const ordersFromOldOf = (orders, newCx) => Math.max(0, orders - newCx);

const retentionCostOf = (row, ordersFromOld) => {
  if (!ordersFromOld) return 0;
  const spend = (Number(row.whatsappAbandon) || 0)
    + (Number(row.whatsappCampaign) || 0)
    + (Number(row.utilityWa) || 0)
    + (Number(row.sms) || 0)
    + (Number(row.email) || 0);
  return round2(spend / ordersFromOld);
};

const applyRates = (row) => {
  const orders = Number(row.orders) || 0;
  const gmv = Number(row.gmv) || 0;
  const newCx = Number(row.newCx) || 0;
  const utilityWa = round2(orders * 3 * 0.13);
  const next = { ...row, utilityWa };
  const overall = SPEND_KEYS.reduce((sum, key) => sum + (Number(next[key]) || 0), 0);
  const ordersFromOld = ordersFromOldOf(orders, newCx);
  const storeCredit = Number(row.storeCredit) || 0;
  const wallet = (Number(row.loyalty) || 0) + storeCredit;
  return {
    ...next,
    overall: round2(overall),
    aov: orders ? round2(gmv / orders) : 0,
    roas: overall ? round2(gmv / overall) : 0,
    costPerOrder: orders ? round2(overall / orders) : 0,
    ecouponPct: gmv ? round2(((Number(row.ecoupon) || 0) / gmv) * 100) : 0,
    loyaltyPct: gmv ? round2(((Number(row.loyalty) || 0) / gmv) * 100) : 0,
    storeCredit,
    storeCreditPct: gmv ? round2((storeCredit / gmv) * 100) : 0,
    wallet: round2(wallet),
    walletPct: gmv ? round2((wallet / gmv) * 100) : 0,
    overallDiscount: gmv ? round2((((Number(row.ecoupon) || 0) + wallet) / gmv) * 100) : 0,
    ordersFromOld,
    retentionCost: retentionCostOf(next, ordersFromOld),
    cac: newCx ? round2(((Number(row.socialMedia) || 0) + (Number(row.googleAds) || 0)) / newCx) : 0,
    ordersFromAppPct: orders ? round2(((Number(row.ordersFromApp) || 0) / orders) * 100) : 0
  };
};

const withTypedValue = (payload, day, field, raw) => {
  if (!payload?.rows) return payload;
  const rows = payload.rows.map((row) => {
    if (row.day !== day) return row;
    const value = field === 'marketingNotes' ? String(raw || '') : (raw === '' ? 0 : Number(raw) || 0);
    return applyRates({ ...row, [field]: value });
  });
  const summed = rows.reduce((sum, row) => {
    SPEND_KEYS.concat(['orders', 'gmv', 'ecoupon', 'loyalty', 'storeCredit', 'newCx', 'oldCx', 'ordersFromApp']).forEach((key) => {
      sum[key] = (Number(sum[key]) || 0) + (Number(row[key]) || 0);
    });
    return sum;
  }, { day: 'Total', weekday: '' });
  return { ...payload, rows, totals: applyRates(summed) };
};

const applyMonthRates = (row) => {
  const rated = applyRates(row);
  const utilityWa = Number(row.utilityWa) || 0;
  const next = { ...rated, utilityWa };
  const overall = SPEND_KEYS.reduce((sum, key) => sum + (Number(next[key]) || 0), 0);
  const orders = Number(row.orders) || 0;
  const gmv = Number(row.gmv) || 0;
  const newCx = Number(row.newCx) || 0;
  const ordersFromOld = ordersFromOldOf(orders, newCx);
  return {
    ...next,
    overall: round2(overall),
    roas: overall ? round2(gmv / overall) : 0,
    costPerOrder: orders ? round2(overall / orders) : 0,
    ordersFromOld,
    retentionCost: retentionCostOf(next, ordersFromOld),
    cac: newCx ? round2(((Number(next.socialMedia) || 0) + (Number(next.googleAds) || 0)) / newCx) : 0
  };
};

const withTypedMonth = (payload, monthKey, field, raw) => {
  if (!payload?.months) return payload;
  const months = payload.months.map((row) => {
    if (row.month !== monthKey) return row;
    const value = field === 'marketingNotes' ? String(raw || '') : (raw === '' ? 0 : Number(raw) || 0);
    return applyMonthRates({ ...row, [field]: value });
  });
  const summed = months.reduce((sum, row) => {
    SPEND_KEYS.concat(['orders', 'gmv', 'ecoupon', 'loyalty', 'storeCredit', 'newCx', 'oldCx', 'ordersFromApp']).forEach((key) => {
      sum[key] = (Number(sum[key]) || 0) + (Number(row[key]) || 0);
    });
    return sum;
  }, { day: 'Total', weekday: '', monthLabel: 'Total' });
  const utilityWa = months.reduce((sum, row) => sum + (Number(row.utilityWa) || 0), 0);
  return {
    ...payload,
    months,
    totals: { ...applyMonthRates({ ...summed, utilityWa }), monthLabel: 'Total' }
  };
};

const formatCell = (column, value) => {
  if (column.kind === 'date') {
    if (value === 'Total') return 'Total';
    return value ? value.slice(8) : '';
  }
  if (column.kind === 'text' || column.kind === 'notes') return value || '';
  if (column.kind === 'count') return money(value);
  if (column.kind === 'ratio') return ratio(value);
  if (column.kind === 'pct') return pct(value);
  return money(value);
};

const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const exportCell = (column, row) => {
  const value = row[column.key];
  if (column.kind === 'date') {
    if (value === 'Total') return 'Total';
    return value || '';
  }
  if (column.kind === 'text' || column.kind === 'notes') return value || '';
  if (value == null || value === '') return '';
  return value;
};

const MarketingSpendAnalysis = () => {
  const { user } = useAuth();
  const [view, setView] = useState('daily');
  const [month, setMonth] = useState(currentMonthKey);
  const [inspectMonth, setInspectMonth] = useState(null);
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingDay, setSavingDay] = useState(null);
  const requestId = useRef(0);
  const liveGen = useRef(0);

  const load = useCallback(async (selectedMonth, refresh = false) => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setError(null);
    try {
      let response = await axios.get('/marketing-spend', {
        params: { month: selectedMonth, refresh: refresh ? '1' : '0' },
        timeout: refresh ? 180000 : 15000
      });
      if (id !== requestId.current) return;
      setData(response.data);
      setLoading(false);
      while (response.data.building && id === requestId.current) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (id !== requestId.current) return;
        response = await axios.get('/marketing-spend', {
          params: { month: selectedMonth },
          timeout: 15000
        });
        if (id !== requestId.current) return;
        setData(response.data);
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.response?.data?.message || 'Could not load marketing spend');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  const loadMonthly = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/marketing-spend/monthly', { timeout: 60000 });
      if (id !== requestId.current) return;
      setMonthly(response.data);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.response?.data?.message || 'Could not load monthly spend');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'monthly') {
      loadMonthly();
      return;
    }
    const openMonth = currentMonthKey();
    setMonth(openMonth);
    load(openMonth, false);
  }, [view, load, loadMonthly]);

  useEffect(() => {
    const timer = setInterval(async () => {
      const active = document.activeElement;
      if (active && active.classList && active.classList.contains('msa-input')) return;
      const gen = liveGen.current;
      try {
        if (inspectMonth) {
          const response = await axios.get('/marketing-spend', {
            params: { month: inspectMonth },
            timeout: 15000
          });
          if (gen !== liveGen.current) return;
          if (response.data?.month !== inspectMonth) return;
          setData(response.data);
          return;
        }
        if (view === 'monthly') {
          const response = await axios.get('/marketing-spend/monthly', { timeout: 60000 });
          if (gen !== liveGen.current) return;
          setMonthly(response.data);
          return;
        }
        const openMonth = currentMonthKey();
        const response = await axios.get('/marketing-spend', {
          params: { month: openMonth },
          timeout: 15000
        });
        if (gen !== liveGen.current) return;
        if (response.data?.month !== openMonth) return;
        setMonth(openMonth);
        setData(response.data);
      } catch (_) {
        // Keep the table. The next minute tries again.
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [view, inspectMonth]);

  const openMonthDays = (monthKey) => {
    liveGen.current += 1;
    setInspectMonth(monthKey);
    setMonth(monthKey);
    load(monthKey, false);
  };

  const closeMonthDays = () => {
    liveGen.current += 1;
    setInspectMonth(null);
    loadMonthly();
  };

  const saveCell = async (day, field, raw) => {
    liveGen.current += 1;
    const body = field === 'marketingNotes'
      ? { marketingNotes: raw }
      : { [field]: raw === '' ? 0 : Number(raw) };
    setSavingDay(day);
    let previous = null;
    setData((current) => {
      previous = current;
      return withTypedValue(current, day, field, raw);
    });
    try {
      const response = await axios.put(`/marketing-spend/${month}/days/${day}`, body, { timeout: 180000 });
      setData(response.data);
    } catch (err) {
      if (previous) setData(previous);
      setError(err.response?.data?.message || 'Could not save that cell');
    } finally {
      setSavingDay(null);
    }
  };

  const saveMonthCell = async (monthKey, field, raw) => {
    liveGen.current += 1;
    const body = field === 'marketingNotes'
      ? { marketingNotes: raw }
      : { [field]: raw === '' ? 0 : Number(raw) };
    setSavingDay(monthKey);
    let previous = null;
    setMonthly((current) => {
      previous = current;
      return withTypedMonth(current, monthKey, field, raw);
    });
    try {
      const response = await axios.put(`/marketing-spend/${monthKey}/total`, body, { timeout: 180000 });
      setMonthly(response.data);
    } catch (err) {
      if (previous) setMonthly(previous);
      setError(err.response?.data?.message || 'Could not save that cell');
    } finally {
      setSavingDay(null);
    }
  };

  const visible = data?.month === month ? data : null;
  const header = visible?.header || {};
  const dailyRows = visible?.rows || [];
  const isMonthlyList = view === 'monthly' && !inspectMonth;
  const canEditInspectedDay = (day) => {
    if (!inspectMonth || user?.role === 'admin') return true;
    return day === lastDayKey(inspectMonth);
  };
  const columns = isMonthlyList ? MONTH_COLUMNS : COLUMNS;
  const rows = isMonthlyList ? (monthly?.months || []) : dailyRows;
  const totals = isMonthlyList ? monthly?.totals : visible?.totals;
  const updatedAt = isMonthlyList ? monthly?.generatedAt : visible?.generatedAt;

  const exportCsv = () => {
    const exportRows = totals ? [totals, ...rows] : rows;
    if (!exportRows.length) return;
    const csv = [
      columns.map((column) => csvCell(column.label)).join(','),
      ...exportRows.map((row) => columns.map((column) => csvCell(exportCell(column, row))).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', isMonthlyList
      ? `marketing_spend_monthly_${stamp}.csv`
      : `marketing_spend_daily_${month}_${stamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="msa-page">
      <Header
        title="Marketing Spend Analysis"
        subtitle={isMonthlyList
          ? 'Completed months, one totals row each'
          : inspectMonth
            ? `Daily spend for ${formatMonth(inspectMonth)}`
            : 'Daily spend, orders, and GMV for the open month'}
        onRefresh={() => (inspectMonth ? load(inspectMonth, false) : isMonthlyList ? loadMonthly() : load(currentMonthKey(), false))}
        loading={loading}
        lastUpdated={updatedAt ? new Date(updatedAt) : new Date()}
      />

      <div className="msa-content">
        <div className="msa-toolbar">
          <div className="msa-views" role="tablist">
            <button
              type="button"
              className={view === 'daily' ? 'is-active' : ''}
              onClick={() => {
                liveGen.current += 1;
                setInspectMonth(null);
                setView('daily');
              }}
            >
              Daily
            </button>
            <button
              type="button"
              className={view === 'monthly' ? 'is-active' : ''}
              onClick={() => {
                if (inspectMonth) {
                  closeMonthDays();
                  return;
                }
                setView('monthly');
              }}
            >
              Monthly
            </button>
          </div>
          {inspectMonth && (
            <button type="button" className="msa-back" onClick={closeMonthDays}>
              Back to monthly
            </button>
          )}
          {isMonthlyList ? (
            <div className="msa-stats">
              <Stat label="Completed months" value={monthly?.months?.length ?? '—'} />
            </div>
          ) : (
            <>
              <div className="msa-month">
                Month
                <strong>{formatMonth(month)}</strong>
              </div>
              <div className="msa-stats">
                <Stat label="Days" value={header.days ?? '—'} />
                <Stat label="Number of days" value={header.numberOfDays ?? '—'} />
                <Stat label="Last month GMV" value={header.lastMonthGmv != null ? money(header.lastMonthGmv) : '—'} />
                <Stat label="Variance" value={header.variance != null ? money(header.variance) : '—'} />
                <Stat label="On track" value={header.onTrack != null ? money(header.onTrack) : '—'} />
              </div>
            </>
          )}
          <button
            type="button"
            className="msa-export"
            onClick={exportCsv}
            disabled={!rows.length && !totals}
          >
            Export
          </button>
        </div>

        {!isMonthlyList && visible?.building && <p className="msa-note">Calculating this month. The table will fill in on its own.</p>}

        {error && <Alert severity="error" message={error} onClose={() => setError(null)} />}

        <div className={`msa-table-wrap${loading ? ' is-loading' : ''}`}>
          <table className={`msa-table${isMonthlyList ? ' is-monthly' : ''}`}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={column.manual ? 'is-manual' : ''}>{column.label}</th>
                ))}
              </tr>
              {totals && (
                <tr className="is-total">
                  {columns.map((column) => (
                    <td key={column.key}>{formatCell(column, totals[column.key])}</td>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.month || row.day} className={savingDay === (isMonthlyList ? row.month : row.day) ? 'is-saving' : ''}>
                  {columns.map((column) => {
                    const editable = column.manual && canEditInspectedDay(row.day);
                    return (
                      <td key={column.key} className={editable ? 'is-manual' : ''}>
                        {isMonthlyList && column.key === 'monthLabel' ? (
                          <span className="msa-month-label">
                            <button
                              type="button"
                              className="msa-eye"
                              title={`Daily view for ${formatMonth(row.month)}`}
                              aria-label={`Daily view for ${formatMonth(row.month)}`}
                              onClick={() => openMonthDays(row.month)}
                            >
                              <Eye size={14} />
                            </button>
                            {formatCell(column, row[column.key])}
                          </span>
                        ) : editable ? (
                          <CellInput
                            column={column}
                            value={row[column.key]}
                            onCommit={(value) => (
                              isMonthlyList
                                ? saveMonthCell(row.month, column.key, value)
                                : saveCell(row.day, column.key, value)
                            )}
                          />
                        ) : (
                          formatCell(column, row[column.key])
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && (
            <p className="msa-empty">
              {isMonthlyList ? 'No completed month yet. The open month stays in Daily until the next month starts.' : 'No rows for this month yet.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="msa-stat">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const CellInput = ({ column, value, onCommit }) => {
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = () => {
    const next = column.kind === 'notes' ? String(draft || '') : String(draft ?? '');
    const current = column.kind === 'notes' ? String(value || '') : String(value ?? 0);
    if (next === current || (column.kind !== 'notes' && Number(next || 0) === Number(value || 0))) return;
    onCommit(column.kind === 'notes' ? next : next);
  };

  if (column.kind === 'notes') {
    return (
      <input
        className="msa-input msa-notes"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
      />
    );
  }

  return (
    <input
      className="msa-input"
      type="number"
      min="0"
      step="1"
      value={draft === 0 ? '' : draft}
      placeholder="0"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
    />
  );
};

export default MarketingSpendAnalysis;
